import { useState } from "react";
import { StickyNote, Undo2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { OPS } from "@/constants/testIds";
import { QuantityInput } from "@/components/waste-meals/QuantityInput";
import { RecordedTodayChip } from "@/components/waste-meals/RecordedTodayChip";
import { NOTES_MAX, countsAsWaste, hasText, isRawItem } from "@/components/waste-meals/wmUtils";

const C = OPS.wm;

/**
 * One day-sheet line. It ALWAYS starts empty (D14) — never prefilled with
 * what is already logged, because that is what made «حفظ» ambiguous.
 *
 * 2026-10-16: a raw-ingredient item carries a «خام» badge (D2); the line has
 * its own note behind «ملاحظة» (A1) — a note travels only with a quantity, so
 * a note on an empty line says so instead of vanishing on save; the return
 * reason «مرتجع للمستودع» is labelled «لا يُحتسب هدراً» wherever it is picked.
 */
export function DaySheetLine({ item, chip, value, reason, note, onChange, onReason, onNote, onOpenPanel,
                              reasons, showReason, error, warnAbove }) {
    const { t, lang } = useI18n();
    const [noteOpen, setNoteOpen] = useState(false);
    const name = lang === "ar" ? item.name_ar || item.name_en : item.name_en || item.name_ar;
    const unit = lang === "ar" ? item.unit_name_ar : item.unit_name_en;
    const reasonName = (r) => (lang === "ar" ? r.name_ar || r.name_en : r.name_en || r.name_ar);
    const picked = (reasons || []).find((r) => r.key === reason);
    const notWaste = !!picked && !countsAsWaste(picked);
    const noteText = note || "";
    const withNote = hasText(noteText);
    const withQty = !!value && Number(value) > 0;

    return (
        <div data-testid={C.sheet.line(item.id)}
             className={`grid grid-cols-[1fr_7.5rem] items-start gap-x-2 gap-y-1.5 border-b border-[color:var(--separator)] py-2 ${
                 error ? "bg-[color:rgba(220,38,38,0.05)]" : ""
             }`}>
            <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[color:var(--ink)]">
                    {name}
                    {isRawItem(item) ? (
                        <Badge data-testid={C.sheet.rawBadge(item.id)} variant="brown"
                               className="ms-1.5 px-1.5 py-0 text-[10px]">
                            {t("wm_raw_badge")}
                        </Badge>
                    ) : null}
                    {item.is_active === false ? (
                        <Badge variant="secondary" className="ms-1.5 px-1.5 py-0 text-[10px]">
                            {t("wm_badge_inactive_item")}
                        </Badge>
                    ) : null}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <RecordedTodayChip chip={chip} itemId={item.id} onOpen={onOpenPanel} />
                    {showReason ? (
                        <Select value={reason || ""} onValueChange={onReason}>
                            <SelectTrigger data-testid={C.sheet.reason(item.id)}
                                           className="h-7 w-auto min-w-[7rem] rounded-full px-2.5 text-[11px]">
                                {/* the trigger shows the plain name; the not-waste chip
                                    beside it carries the label, so the pill stays short */}
                                <SelectValue placeholder={t("wm_reason")}>
                                    {picked ? reasonName(picked) : undefined}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {(reasons || []).map((r) => (
                                    <SelectItem key={r.key} value={r.key}>
                                        {reasonName(r)}
                                        {!countsAsWaste(r) ? (
                                            <span className="ms-1.5 text-[10px] font-semibold text-[color:var(--brand-brown)]">
                                                · {t("wm_reason_not_waste")}
                                            </span>
                                        ) : null}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : null}
                    {showReason && notWaste ? (
                        <span data-testid={C.sheet.reasonNotWaste(item.id)}
                              className="inline-flex h-7 items-center gap-1 rounded-full bg-[color:var(--brand-brown-soft)] px-2 text-[10px] font-semibold text-[color:var(--brand-brown-soft-fg)]">
                            <Undo2 className="h-3 w-3" />
                            {t("wm_reason_not_waste")}
                        </span>
                    ) : null}
                    <button type="button" data-testid={C.sheet.noteToggle(item.id)}
                            data-active={withNote ? "true" : "false"} aria-expanded={noteOpen}
                            onClick={() => setNoteOpen((v) => !v)}
                            className={`inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-[11px] font-semibold transition-colors duration-150 ${
                                withNote
                                    ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-brown)]"
                                    : "border-[color:var(--border-soft)] text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                            }`}>
                        <StickyNote className="h-3.5 w-3.5" />
                        {t("wm_note_add")}
                    </button>
                </div>
                {!noteOpen && withNote ? (
                    <button type="button" data-testid={C.sheet.notePreview(item.id)} onClick={() => setNoteOpen(true)}
                            className="mt-1 block max-w-full truncate text-start text-[11px] text-[color:var(--ink-muted)] hover:underline">
                        <bdi>{noteText}</bdi>
                    </button>
                ) : null}
                {error ? (
                    <p className="mt-1 text-[11px] font-semibold text-[color:var(--danger)]">
                        {lang === "ar" ? error.message_ar : error.message_en}
                    </p>
                ) : null}
            </div>
            <QuantityInput testId={C.sheet.qty(item.id)} value={value} onChange={onChange}
                           unitLabel={unit} allowsDecimal={!!item.allows_decimal}
                           warnAbove={warnAbove} invalid={!!error} />
            {noteOpen ? (
                <div className="col-span-2 min-w-0">
                    <Input data-testid={C.sheet.note(item.id)} value={noteText} autoFocus dir="auto"
                           maxLength={NOTES_MAX} onChange={(e) => onNote(e.target.value)}
                           placeholder={t("wm_note_placeholder")} aria-label={t("wm_notes")}
                           className="h-10 text-base" />
                    {noteText.length > NOTES_MAX - 50 ? (
                        <p className="mt-0.5 text-end text-[10px] text-[color:var(--ink-faint)]">
                            <bdi dir="ltr">{noteText.length}/{NOTES_MAX}</bdi>
                        </p>
                    ) : null}
                </div>
            ) : null}
            {withNote && !withQty ? (
                <p data-testid={C.sheet.noteNeedsQty(item.id)}
                   className="col-span-2 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                    {t("wm_note_needs_qty")}
                </p>
            ) : null}
        </div>
    );
}
